/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import setFavouriteStore from '@tcp/core/src/queries/setFavouriteStore.query.graphql';
import getFavouriteCustomerStore from '@tcp/core/src/queries/getFavouriteStore.query.graphql';
import logger from '@tcp/core/src/utils/loggerInstance';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import {
  executeExternalAPICall,
  executeStatefulAPICall,
  executeStatefulGraphQLQuery,
} from '../../../handler';
import { sanitizeEntity, getAPIConfig, parseBoolean, getDateObject } from '../../../../utils';
import { formatPhoneNumber } from '../../../../utils/formValidation/phoneNumber';
import { parseStoreHours } from '../../../../utils/parseStoreHours';
import endpoints from '../../../endpoints';
import { showLSPMEnabled } from '../../../../reduxStore/selectors/session.selectors';

const DEFAULT_RADIUS = 75;
const STORE_TYPES = {
  RETAIL: 'Retail Store',
  OUTLET: 'Outlet',
};
const BOPIS_ITEM_AVAILABILITY = {
  AVAILABLE: 'OK',
  LIMITED: 'LIMITED',
  UNAVAILABLE: 'UNAVAILABLE',
};

/**
 * @function errorHandler function to handle all the server side errors.
 * @param {object} err - error object in case server side data send server side validation errors.
 * @returns {object} error object with appropirate error message
 */
const errorHandler = (err) => {
  return err || null;
};

/**
 * @function getAddress - returns the store address section
 * @param {*} storeDetails - server response
 */
export const getAddress = (storeDetails) => {
  const {
    address1,
    streetLine1,
    addressLine,
    city,
    stateOrProvinceName,
    state,
    country,
    zipCode,
    postalCode,
    postalcode,
    zipcode,
  } = storeDetails;
  const addressLine1 = sanitizeEntity(address1 || streetLine1 || (addressLine && addressLine[0]));
  return {
    addressLine1,
    city,
    state: stateOrProvinceName || state,
    country,
    zipCode: (zipCode || postalCode || postalcode || zipcode).trim(),
  };
};

/**
 * @function getAddress - returns the store address section
 * @param {*} storeDetails - server response
 */
export const getAddressMapbox = (storeDetails) => {
  const { addressLine, city, state, country, zipCode } = storeDetails;
  const addressLine1 = sanitizeEntity(addressLine);
  return {
    addressLine1,
    city,
    state,
    country,
    zipCode,
  };
};

/**
 * @function getStoreNameVal - returns the store name
 * @param {Object} storeDetails - server response
 */
export const getStoreNameVal = (storeDetails) => {
  return sanitizeEntity(
    (storeDetails.description &&
      (storeDetails.description.displayStoreName ||
        (storeDetails.description[0] && storeDetails.description[0].displayStoreName))) ||
      (storeDetails.Description &&
        storeDetails.Description[0] &&
        storeDetails.Description[0].displayStoreName) ||
      storeDetails.storeName ||
      storeDetails.name ||
      ''
  );
};

/**
 * @function getStoreNameVal - returns the store name
 * @param {Object} storeDetails - server response
 */
export const getStoreNameValMapbox = (storeDetails) => {
  return sanitizeEntity(storeDetails.name || storeDetails.storeName || '');
};

/**
 * @function getBasicInfo - parse the server response and only returns the store basic info
 * @param {Object} storeDetails - server response
 */
export const getBasicInfo = (storeDetails, brand, country, stlocId, isStoreInfoApi) => {
  const id =
    storeDetails.uniqueId ||
    storeDetails.storeLocId ||
    storeDetails.storeUniqueID ||
    storeDetails.stLocId ||
    storeDetails.uniqueID;
  // Was getting exception when app loads as storeDetails was empty object
  if (id) {
    const storeNameVal = getStoreNameVal(storeDetails);
    const phoneNumber =
      formatPhoneNumber(storeDetails.telephone1 || storeDetails.phone || storeDetails.phone1) || '';
    return {
      id: id.toString(),
      storeName: storeNameVal,
      isDefault: storeDetails.preferredStore || false,
      address: getAddress(storeDetails),
      phone: phoneNumber,
      coordinates: {
        lat: parseFloat(storeDetails.latitude),
        long: parseFloat(storeDetails.longitude),
      },
    };
  }
  if (isStoreInfoApi) {
    logger.info('No address found for the provided storeId', brand, country, stlocId);
  }
  return {};
};

export const parseStoreLocationJSON = (jsonString) => {
  try {
    const parsedData = JSON.parse(jsonString);
    if (parsedData.storelocation && Array.isArray(parsedData.storelocation)) {
      return parsedData.storelocation;
    }
    logger.error('Invalid JSON structure: Missing or incorrect "storelocation" array');
    return null;
  } catch (error) {
    logger.error('Error parsing JSON:', error.message);
    return null;
  }
};

/**
 * @function getBasicInfo - parse the server response and only returns the store basic info
 * @param {Object} storeDetails - server response
 */
export const getBasicInfoMapbox = (storeDetails) => {
  const id = storeDetails.storeLocId.toString();
  const storeNameVal = getStoreNameValMapbox(storeDetails);
  const phoneNumber = formatPhoneNumber(storeDetails.telephone.toString()) || '';
  const storeDiscount = storeDetails.storeDiscount?.toString();
  const { storeLocation = '' } = storeDetails || {};
  return {
    id,
    storeName: storeNameVal,
    isDefault: storeDetails.preferredStore || false,
    address: getAddressMapbox(storeDetails),
    storeLocation: storeLocation && parseStoreLocationJSON(storeLocation),
    phone: phoneNumber,
    coordinates: {
      lat: parseFloat(storeDetails.latitude),
      long: parseFloat(storeDetails.longitude),
    },
    storeDiscount,
  };
};

/**
 * @function getDistance returns the distance
 * @param {*} distance
 * @param {*}  distanceFromUserToStore - user distance from store
 */
export const getDistance = ({ distance, distanceFromUserToStore }) => {
  if (distance) {
    return parseFloat(distance).toFixed(2);
  }
  if (distanceFromUserToStore) {
    return parseFloat(distanceFromUserToStore).toFixed(2);
  }
  return null;
};

/**
 * @function getDistance returns the distance
 * @param {*} distance
 * @param {*}  distanceFromUserToStore - user distance from store
 */
export const getDistanceMapbox = ({ distance }) => {
  if (distance) {
    return parseFloat(distance).toFixed(2);
  }

  return null;
};

/**
 * @function getStoreTypeDetail - return the store type
 * @param {Object} - server response
 */
export const getStoreTypeDetail = ({ storeType, addressLineDetail, address3 }) => {
  return (
    storeType ||
    (addressLineDetail && addressLineDetail[addressLineDetail.length - 1]) ||
    address3 ||
    ''
  );
};

/**
 * @function getStoreStatus - store status whether Available, UnAvailable or limited.
 * @param {object} storeDetails - server response
 * @param {Number} requestedQuantity - quantity
 */
export const getStoreStatus = (storeDetails, requestedQuantity) => {
  if (storeDetails.itemAvailability[0].qty < requestedQuantity) {
    return BOPIS_ITEM_AVAILABILITY.UNAVAILABLE;
  }
  if (storeDetails.itemAvailability[0].itemStatus === 'AVAILABLE') {
    return BOPIS_ITEM_AVAILABILITY.AVAILABLE;
  }
  if (storeDetails.itemAvailability[0].itemStatus === 'UNAVAILABLE') {
    return BOPIS_ITEM_AVAILABILITY.UNAVAILABLE;
  }
  return BOPIS_ITEM_AVAILABILITY.LIMITED;
};

/**
 * @function getStoreParameters - return the store details
 * @param {Object} - server response
 */
export const getStoreParametersMapbox = ({ storeHoursMapBoxWCS, storeDetails, addressLine }) => {
  let hoursOfOperation;
  const addressLineDetail = addressLine;
  let isGym;
  if (storeHoursMapBoxWCS) {
    const storeHours = JSON.parse(storeHoursMapBoxWCS);
    hoursOfOperation = storeHours.storehours;
  }
  if (storeDetails.brand) {
    isGym = storeDetails.brand.includes('GYM') || storeDetails.brand === 'ALL';
  }
  return {
    addressLineDetail,
    hoursOfOperation,
    isGym,
  };
};

/**
 * @function getStoreParameters - return the store details
 * @param {Object} - server response
 */
export const getStoreParameters = ({
  storehours,
  Attribute,
  storeDetails,
  addressLine,
  attribute,
}) => {
  let hoursOfOperation;
  let addressLineDetail;
  let isGym;

  // Sometimes addressLine is returned as an array
  // Sometimes addressLine is returned as an object with numerical properties (WHY???)
  // If addressLine is object, convert to array
  if (addressLine && typeof addressLine === 'object' && !Array.isArray(addressLine)) {
    addressLineDetail = Object.keys(addressLine).map((key) => addressLine[key]);
  }

  // Backend's API structure for stores are never the same, so i am checking a few differant places for storeDetails hours
  if (storehours) {
    hoursOfOperation = storehours.storehours;
  }
  if (Attribute && Attribute[0]) {
    hoursOfOperation = JSON.parse(Attribute[0].displayValue || '{}').storehours;
  }
  if (storeDetails.attribute) {
    hoursOfOperation = JSON.parse(attribute.displayValue || '{}').storehours;
    isGym = attribute.brands.includes('GYM');
  }
  return {
    addressLineDetail,
    hoursOfOperation,
    isGym,
  };
};

/**
 * @function storeResponseParser - to parse the server response and pass it UI components
 * @param {Object} storeDetails - Server response
 * @param {Object} configs - config data
 */
export const storeResponseParser = (storeDetails, configs = { requestedQuantity: 0 }) => {
  const { requestedQuantity } = configs;
  const {
    addressLine,
    storeType,
    address3,
    storehours,
    Attribute,
    attribute,
    isStoreBOSSEligible,
    bossMinDate,
    bossMaxDate,
    isStoreBossSelected,
    isStoreBopisSelected,
    itemAvailability,
  } = storeDetails;
  const { hoursOfOperation, addressLineDetail, isGym } = getStoreParameters({
    storehours,
    Attribute,
    storeDetails,
    addressLine,
    attribute,
  });

  // Sometimes storeType is explicitly defined
  // Sometimes storeType needs to be determined using address
  const storeTypeDetail = getStoreTypeDetail({ storeType, addressLineDetail, address3 });

  // Parse Store Info
  const storeFilteredInfo = {
    // Boss storeDetails info
    storeBossInfo: {
      isBossEligible: isStoreBOSSEligible,
      startDate: bossMinDate,
      endDate: bossMaxDate,
    },
    /** added storeType | also checking if the flag is undefined than the value should be true
     * for default searching without any restriction
     */
    pickupType: {
      isStoreBossSelected: isStoreBossSelected !== undefined ? isStoreBossSelected : true,
      isStoreBopisSelected: isStoreBopisSelected !== undefined ? isStoreBopisSelected : true,
    },
    distance: getDistance(storeDetails),
    basicInfo: getBasicInfo(storeDetails),
    isGym,
    hours: {
      regularHours: [],
      holidayHours: [],
      regularAndHolidayHours: [],
    },
    features: {
      storeType:
        STORE_TYPES[storeTypeDetail] || (storeTypeDetail === 'PLACE' && STORE_TYPES.RETAIL) || '',
      mallType: storeDetails.x_mallType,
      entranceType: storeDetails.x_entranceType,
    },
    productAvailability:
      itemAvailability && itemAvailability[0]
        ? {
            skuId: itemAvailability[0].itemId,
            status: getStoreStatus(storeDetails, requestedQuantity),
            quantity: itemAvailability[0].qty,
          }
        : {},
  };
  // Parse Store Hours
  if (hoursOfOperation && hoursOfOperation.length) {
    storeFilteredInfo.hours.regularHours = parseStoreHours(hoursOfOperation);
  }
  return storeFilteredInfo;
};

export const cleanStr = (str) => str.replace(/-| |\./g, '');

/**
 * Parses the store message based on the store state and LSPM switch.
 *
 * @param {Function} getStoreRef - Function to get the store reference.
 * @param {Function} showLSPMEnabled - Function to check if LSPM is enabled.
 * @param {string} storeMessage - The store message to parse.
 * @returns {string} - The parsed store message or an empty string if conditions are not met.
 */
const parseStoreMessageFunc = (storeMessage) => {
  if (getStoreRef()?.getState()) {
    const lspmSwitch = showLSPMEnabled(getStoreRef().getState());
    if (lspmSwitch && storeMessage) {
      try {
        const parsedMessage = JSON.parse(storeMessage);
        return parsedMessage.storemessage || '';
      } catch (error) {
        return '';
      }
    }
  }
  return '';
};

/**
 * @function storeResponseParser - to parse the server response and pass it UI components
 * @param {Object} storeDetails - Server response
 * @param {Object} configs - config data
 */
export const storeResponseParserMapbox = (storeData) => {
  const { properties: storeDetails, akamaiBased } = storeData;
  const {
    addressLine,
    storeType,
    storehours,
    storehoursjson,
    tilequery,
    MIN_DATE: bossMinDate, // Boss EDD Min date
    MAX_DATE: bossMaxDate, // Boss EDD Max date
    bosseligible,
    HOLIDAYSTOREHOURSJSON,
    storeMessage,
  } = storeDetails;

  const storeHoursMapBoxWCS = storehours || storehoursjson;
  const holidayHoursMapBox = HOLIDAYSTOREHOURSJSON;
  let holidayHoursMapBoxHoursOfOperation;
  if (holidayHoursMapBox) {
    const storeHours = JSON.parse(HOLIDAYSTOREHOURSJSON);
    holidayHoursMapBoxHoursOfOperation = storeHours.holidaystorehours;
  }
  const { hoursOfOperation, isGym } = getStoreParametersMapbox({
    storeHoursMapBoxWCS,
    storeDetails,
    addressLine,
  });
  const parsedStoreMsg = parseStoreMessageFunc(storeMessage);

  // Parse Store Info
  const storeFilteredInfo = {
    distance: getDistanceMapbox(tilequery),
    basicInfo: getBasicInfoMapbox(storeDetails),
    storeBossInfo: {
      startDate: bossMinDate,
      endDate: bossMaxDate,
      isBossEligible: parseBoolean(bosseligible),
    },
    isGym,
    hours: {
      regularHours: [],
      holidayHours: [],
      regularAndHolidayHours: [],
    },
    storeMessage: parsedStoreMsg,
    features: {
      bossMinDate,
      bossMaxDate,
      storeType: STORE_TYPES[storeType] || (storeType === 'PLACE' && STORE_TYPES.RETAIL) || '',
      mallType: storeDetails.x_mallType,
      entranceType: storeDetails.x_entranceType,
      redirectURL: `${cleanStr(storeDetails?.storeName)}-${cleanStr(storeDetails?.city)}-${cleanStr(
        storeDetails?.addressLine
      )}-${storeDetails?.state}-${storeDetails?.storeLocId}`,
    },
    akamaiBased,
  };
  // Parse Store Hours
  if (hoursOfOperation && hoursOfOperation.length) {
    storeFilteredInfo.hours.regularHours = parseStoreHours(hoursOfOperation);
  }
  if (holidayHoursMapBoxHoursOfOperation && holidayHoursMapBoxHoursOfOperation.length) {
    storeFilteredInfo.hours.holidayHours = parseStoreHours(holidayHoursMapBoxHoursOfOperation);
  }
  return storeFilteredInfo;
};

export const storeResponseParserSfcc = (storeData) => {
  const {
    c_MAX_DATE: MAX_DATE,
    c_MIN_DATE: MIN_DATE,
    address1: addressLine,
    c_IS_STORE_BOSS_ELIGIBLE: bosseligible,
    c_brand: brand,
    city,
    country_code: country,
    store_hours: storehoursjson,
    c_DC_PROCESSING_TIME: dcprocessingtime,
    c_entranceType: entranceType,
    latitude,
    longitude,
    c_mallType: mallType,
    c_MAX_NUMBER_OF_DAYS: maxdays,
    c_MIN_NUMBER_OF_DAYS: mindays,
    c_SHIP_SCHEDULE_FRI: SHIP_SCHEDULE_FRI,
    c_SHIP_SCHEDULE_MON: SHIP_SCHEDULE_MON,
    c_SHIP_SCHEDULE_SAT: SHIP_SCHEDULE_SAT,
    c_SHIP_SCHEDULE_SUN: SHIP_SCHEDULE_SUN,
    c_SHIP_SCHEDULE_THU: SHIP_SCHEDULE_THU,
    c_SHIP_SCHEDULE_TUE: SHIP_SCHEDULE_TUE,
    c_SHIP_SCHEDULE_WED: SHIP_SCHEDULE_WED,
    state_code: state,
    id: storeLocId,
    name: storeName,
    c_STORE_BRAND_TYPE: storeType,
    c_STORE_PROCESSING_TIME: storeprocessingtime,
    phone: telephone,
    postal_code: zipCode,
    distance,
    c_storeDiscount: storeDiscount,
  } = storeData;
  const storeDataAsMapBox = {
    properties: {
      MAX_DATE,
      MIN_DATE,
      addressLine,
      bosseligible: bosseligible === '1' ? 'Y' : 'N',
      brand,
      city,
      country,
      storehoursjson,
      dcprocessingtime,
      entranceType,
      latitude,
      longitude,
      mallType,
      maxdays,
      mindays,
      shipschedule: `SHIP_SCHEDULE_FRI:${SHIP_SCHEDULE_FRI};SHIP_SCHEDULE_MON:${SHIP_SCHEDULE_MON};SHIP_SCHEDULE_SAT:${SHIP_SCHEDULE_SAT};SHIP_SCHEDULE_SUN:${SHIP_SCHEDULE_SUN};SHIP_SCHEDULE_THU:${SHIP_SCHEDULE_THU};SHIP_SCHEDULE_TUE:${SHIP_SCHEDULE_TUE};SHIP_SCHEDULE_WED:${SHIP_SCHEDULE_WED}`,
      state,
      storeLocId,
      storeName,
      storeType,
      storeprocessingtime,
      telephone,
      zipCode,
      tilequery: {
        distance,
        geometry: '',
        layer: '',
      },
      storeDiscount,
    },
  };
  return storeResponseParserMapbox(storeDataAsMapBox);
};

/**
 * @function getFavoriteStore
 * @summary This will get a users favorite store that is saved on their account,
 *  if the user's favorite store doesn't exist then
 *  default store on the basis of lat long of user is fetched.
 * @return empty object if you do not have a default store else you will get back
 */
export const getFavoriteStore = () => {
  return executeStatefulGraphQLQuery({
    query: getFavouriteCustomerStore,
  })
    .then((res) => {
      const { getCustomer } = res.data;
      return getCustomer.c_favoriteStore;
    })
    .catch(errorHandler);
};

/**
 * @function getLocationStores
 * @summary given a lat and lng, and an optional radius, this will get all stores in the given area
 */

export const getLocationStoresWCS = ({
  coordinates: { lat, lng },
  maxItems,
  radius = DEFAULT_RADIUS,
}) => {
  const payload = {
    header: {
      latitude: lat,
      longitude: lng,
      radius,
      maxItems,
    },
    body: {},
    webService: endpoints.findStoresByCoordinates,
  };
  return executeStatefulAPICall(payload)
    .then((res) => {
      // unknown structure of response may be nested arrays, may not be nested arrays. all depends on how backend is feeling today.
      const fetchedStores =
        res.body.PhysicalStore &&
        res.body.PhysicalStore[0] &&
        (res.body.PhysicalStore[0].uniqueId || res.body.PhysicalStore[0].uniqueID)
          ? res.body.PhysicalStore
          : (res.body.PhysicalStore && res.body.PhysicalStore[0]) || [];

      if (!fetchedStores.length) {
        errorHandler({ errorCode: 'NO_STORES_FOUND' });
      }
      return fetchedStores.map(storeResponseParser);
    })
    .catch((err) => {
      errorHandler(err);
      return {};
    });
};

/**
 * @function getLocationStores
 * @summary given a lat and lng, and an optional radius, this will get all stores in the given area
 */
export const getLocationStoresMapbox = ({
  coordinates: { lat, lng },
  maxItems,
  radius = DEFAULT_RADIUS,
  fromNearByStore = false,
  storeLocatorMapboxTileIDs,
}) => {
  const numOfItems = !fromNearByStore && maxItems === 5 ? 25 : maxItems;
  const apiUrl = `${
    getAPIConfig().mapboxStoreLocatorApiURL
  }/${storeLocatorMapboxTileIDs}/tilequery/${lng},${lat}.json?radius=${
    radius * 1000
  }&limit=${numOfItems}&access_token=${getAPIConfig().mapBoxApiKey}`;

  const payload = {
    webService: {
      URI: apiUrl,
      method: 'GET',
    },
  };

  return executeExternalAPICall(payload)
    .then((res) => {
      let fetchedStores = res.body.features || [];
      if (!fetchedStores.length) {
        errorHandler({ errorCode: 'NO_STORES_FOUND' });
      }
      fetchedStores = [
        ...new Map(fetchedStores.map((store) => [store?.properties?.storeLocId, store])).values(),
      ];
      return fetchedStores.map(storeResponseParserMapbox);
    })
    .catch((err) => {
      errorHandler(err);
      return {};
    });
};

export const getLocationStoresSfcc = ({
  coordinates,
  maxItems,
  radius = DEFAULT_RADIUS,
  fromNearByStore = false,
}) => {
  const { lat, lng, country_code: cnd } = coordinates;
  if (!lat || !lng) {
    return {};
  }
  let countryQuery = '';
  if (cnd) {
    countryQuery = `&country_code=${cnd}`;
  }
  const numOfItems = !fromNearByStore && maxItems === 5 ? 25 : maxItems;
  const { URI, method } = endpoints.sfccStoreLocatorApiURL;
  const apiUrl = `${URI}?distance_unit=mi&max_distance=${radius}${countryQuery}&latitude=${lat}&longitude=${lng}&count=${numOfItems}`;
  const payload = {
    webService: {
      URI: apiUrl,
      method,
    },
  };

  return executeStatefulAPICall(payload, null, true)
    .then((res) => {
      const fetchedStores = res?.body?.data || [];
      return fetchedStores.map(storeResponseParserSfcc);
    })
    .catch((err) => {
      errorHandler(err);
      return {};
    });
};

/**
 * @function getLocationStores
 * @summary given a lat and lng, and an optional radius, this will get all stores in the given area
 */

export const getLocationStores = (payload, storeLocatorSwitches) => {
  const { storeLocatorMapboxApi, type, mapboxSwitch, akamaiBasedLogic } = payload;
  const { FETCH_CLOSEST_STORE, SWITCH_TO_SFCC_FOR_STORE_INFO } = storeLocatorSwitches;
  let storesData;
  if (akamaiBasedLogic || (mapboxSwitch && storeLocatorMapboxApi && type === 'STORES')) {
    if (payload?.fromAppLaunch || (FETCH_CLOSEST_STORE && SWITCH_TO_SFCC_FOR_STORE_INFO)) {
      storesData = getLocationStoresSfcc(payload);
    } else {
      storesData = getLocationStoresMapbox(payload);
    }
  }
  return storesData;
};

/**
 * @function setFavoriteStore - API to set the favorite store based on user interaction with the stores.
 * @param {String} storeId - id of the favorite store
 * @param {map} state - current state tree.
 */
export const setFavoriteStore = (storeId, state, suggestedStore) => {
  const favStore = suggestedStore && {
    ...suggestedStore,
    timeStamp: getDateObject().getTime(),
    basicInfo: {
      ...suggestedStore.basicInfo,
      isDefault: 1,
    },
  };

  return executeStatefulGraphQLQuery({
    query: setFavouriteStore,
    variables: {
      addFavoriteStoreLocationStorelocid: storeId,
    },
    type: 'mutation',
  })
    .then(() => {
      return favStore;
    })
    .catch(errorHandler);
};
